import { IndexedDBStoreConfig } from '../../typing/IndexedDB-store-config.interface';
import { Injectable } from '@angular/core';
import { MessageService } from '../../services/message.service';

@Injectable({
    providedIn: 'root',
})
export class IndexedDbService {
    private dbName = 'OfflineDB';

    /** Схема базы данных */
    private stores: IndexedDBStoreConfig[] = [
        {
            name: 'equipment',
            keyPath: 'id',
        },
        {
            name: 'files',
            keyPath: 'id',
            indexes: [{ name: 'orderId', keyPath: 'orderId' }],
        },
    ];

    constructor(private messageService:MessageService) {}

    /** Открытие базы данных */
    private async openDB(versionDB: number): Promise<IDBDatabase> {

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, versionDB);

            request.onupgradeneeded = (event: IDBVersionChangeEvent): void => {
                const db = (event.target as IDBOpenDBRequest).result;

                this.stores.forEach(storeConfig => {
                    if (!db.objectStoreNames.contains(storeConfig.name)) {
                        const store = db.createObjectStore(storeConfig.name, { keyPath: storeConfig.keyPath });
                        storeConfig.indexes?.forEach(idx => store.createIndex(idx.name, idx.keyPath, idx.options || { unique: false }));
                    }
                });
            };
            //
            request.onsuccess = (): void => {
                resolve(request.result);
            };

            request.onerror = (): void => {
                this.messageService.errorMessage(`${ JSON.stringify(request.error) } Открытие базы данных`);
                reject(request.error);
            };
        });
    }

    /** Получить объект store */
    private async getStore(
        storeName: string,
        mode: IDBTransactionMode = 'readwrite'
    ): Promise<IDBObjectStore | null> {
        const isSafari =
            (
                /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
                /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent) ||
                /Macintosh.*AppleWebKit(?!.*Chrome)/i.test(navigator.userAgent)
            ) &&
            !/CriOS|Chrome|Edg|Firefox|OPR/i.test(navigator.userAgent);
        /** проверки для определения если safari*/

        const currentVersion = await this.getCurrentDBVersion();
        const targetVersion = isSafari ? 3 : 2; // Safari → 3, остальные → 2

        // Если версия отличается, сбрасываем базу
        if (targetVersion !== currentVersion) {
            await this.resetDatabase();
            /** для смены версии indexDB если */
        }

        let db: IDBDatabase;

        try {
            db = await this.openDB(targetVersion);
        } catch (error) {
            // Если Safari и не удалось открыть v3 — пробуем v2
            if (isSafari && targetVersion === 3) {
                db = await this.openDB(2);
            } else {
                this.messageService.errorMessage(`${JSON.stringify(error)}`);
                throw error;
            }
        }

        return db.transaction([storeName], mode).objectStore(storeName);
    }

    private async resetDatabase(): Promise<void> {
        return new Promise((resolve, reject) => {
            const deleteRequest = indexedDB.deleteDatabase(this.dbName);

            deleteRequest.onsuccess = (): void => resolve();
            deleteRequest.onerror = (): void => reject(deleteRequest.error);
        });
    }

    private async getCurrentDBVersion(): Promise<number | null> {
        return new Promise((resolve) => {
            const request = indexedDB.open(this.dbName);

            request.onsuccess = (): void => {
                const db = request.result;
                const version = db.version;
                db.close();
                resolve(version);
            };

            request.onerror = (): void => {
                resolve(null); // Если не удалось получить версию
            };
        });
    }

    /** Добавить запись */
    public async add<T>(storeName: string, data: T): Promise<void> {
        const store = await this.getStore(storeName, 'readwrite');
        if (!store) {
            return;
        }

        return new Promise((resolve, reject) => {
            const request = store.add(data);
            request.onsuccess = ():void => resolve();
            request.onerror = ():void => {
                reject(request.error);
            };
        });
    }

    /** Обновить запись */
    public async put<T>(storeName: string, data: T): Promise<void> {
        const store = await this.getStore(storeName, 'readwrite');
        if (!store) {
            return;
        }

        return new Promise((resolve, reject) => {
            const request = store.put(data);
            request.onsuccess = (): void => resolve();
            request.onerror = (): void => {
                reject(request.error);
            };
        });
    }

    public async getAllKeysByIndex(storeName: string, indexName: string, value: number | string): Promise<IDBValidKey[]> {
        const store = await this.getStore(storeName);
        if (!store) {
            return [];
        }

        const index = store.index(indexName);

        return new Promise((resolve, reject) => {
            const request = index.getAllKeys(value);
            request.onsuccess = ():void => resolve(request.result);
            request.onerror = ():void => {
                reject(request.error);
            };
        });
    }

    /** Получить по ключу */
    public async get<T>(storeName: string, key: IDBValidKey): Promise<T | null> {
        const store = await this.getStore(storeName);
        if (!store) {
            return null;
        }

        return new Promise((resolve, reject) => {
            const request = store.get(key);
            request.onsuccess = (): void => resolve(request.result || null);
            request.onerror = ():void => {
                reject(request.error);
            };
        });
    }

    /** Получить все записи */
    public async getAll<T>(storeName: string): Promise<T[]> {
        const store = await this.getStore(storeName);
        if (!store) {
            return [];
        }
        return new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = (): void => resolve(request.result);
            request.onerror = ():void => {
                reject(request.error);
            };
        });
    }

    /** Получить все по индексу */
    public async getAllByIndex<T>(storeName: string, indexName: string, indexValue: number | string): Promise<T[]> {
        const store = await this.getStore(storeName);
        if (!store) {
            return [];
        }
        const index = store.index(indexName);

        return new Promise((resolve, reject) => {
            const request = index.getAll(indexValue);
            request.onsuccess = (): void => resolve(request.result);
            request.onerror = ():void => {
                reject(request.error);
            };
        });
    }

    /** Удалить по ключу */
    public async delete(storeName: string, key: IDBValidKey): Promise<void> {
        const store = await this.getStore(storeName, 'readwrite');
        if (!store) {
            return;
        }

        return new Promise((resolve, reject) => {
            const request = store.delete(key);
            request.onsuccess = ():void => resolve();
            request.onerror = ():void => {
                return  reject(request.error);
            };
        });
    }

    /** Очистить store */
    public async clear(storeName: string): Promise<void> {
        const store = await this.getStore(storeName, 'readwrite');
        if (!store) {
            return;
        }

        return new Promise((resolve, reject) => {
            const request = store.clear();
            request.onsuccess = ():void => resolve();
            request.onerror = ():void => {
                return  reject(request.error);
            };
        });
    }

    /** Удалить всю базу */
    public async deleteDatabase(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.deleteDatabase(this.dbName);

            request.onsuccess = (): void => {
                resolve();
            };

            request.onerror = (): void => {
                // Передаем конкретную ошибку в reject
                this.messageService.errorMessage(`${JSON.stringify(request.error)} clear, Очистить store`);
                reject(request.error ?? new Error('Unknown error during database deletion'));
            };

            request.onblocked = (): void => {
                this.messageService.errorMessage('Database deletion blocked. Please close all tabs using this database.');
                reject(new Error('Database deletion blocked. Please close all tabs using this database.'));
            };
        });
    }

    public async hasRecords(storeName: string): Promise<boolean> {
        const store = await this.getStore(storeName);
        if (!store) {
            return false;
        }

        return new Promise((resolve, reject) => {
            const request = store.openCursor();
            request.onsuccess = (): void => {
                const cursor = request.result;
                resolve(!!cursor);
            };
            request.onerror = (): void => {
                reject(request.error);
            };
        });
    }

    async getLastItem<T>(storeName: string): Promise<T | null> {
        const store = await this.getStore(storeName);
        if (!store) {
            return null;
        }

        return new Promise((resolve, reject) => {
            const request = store.openCursor(null, 'prev');
            request.onsuccess = (): void => {
                const cursor = request.result;
                resolve(cursor ? cursor.value : null);
            };
            request.onerror = (): void => {
                reject(request.error);
            };
        });
    }

}
